import PropTypes from 'prop-types';
import { isDisplayableMessage } from '../../utils/apiError';

const FALLBACK_MESSAGE = 'An unexpected error occurred. Please try again.';

// Only short plain-text messages are shown; anything else (HTML error pages,
// stack dumps, huge payloads) falls back to a generic message.
export const getErrorMessage = (error) => {
  const candidate = typeof error === 'string' ? error : (error?.error || error?.message);
  return isDisplayableMessage(candidate) ? candidate : FALLBACK_MESSAGE;
};

const ErrorAlert = ({
  error,
  onRetry,
  onDismiss,
  title = 'Something went wrong',
  showRetry = true,
  showDismiss = true,
}) => (
  <div className="alert alert-danger" role="alert">
    <div className="d-flex align-items-start">
      <div className="flex-grow-1">
        <h4 className="alert-heading h6 mb-2">
          <i className="fas fa-exclamation-triangle mr-2" aria-hidden="true"></i>
          {title}
        </h4>
        <p className="mb-0">{getErrorMessage(error)}</p>
      </div>

      <div className="ml-3 d-flex align-items-center">
        {showRetry && onRetry && (
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            onClick={onRetry}
          >
            <i className="fas fa-redo mr-1" aria-hidden="true"></i>
            Try again
          </button>
        )}

        {showDismiss && onDismiss && (
          <button
            type="button"
            className="close ml-2"
            onClick={onDismiss}
            aria-label="Dismiss error"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        )}
      </div>
    </div>

    {process.env.NODE_ENV === 'development' && error?.stack && (
      <details className="mt-3">
        <summary className="text-muted small">Error details (development only)</summary>
        <pre className="mt-2 small text-muted">{error.stack}</pre>
      </details>
    )}
  </div>
);

ErrorAlert.propTypes = {
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]).isRequired,
  onRetry: PropTypes.func,
  onDismiss: PropTypes.func,
  title: PropTypes.string,
  showRetry: PropTypes.bool,
  showDismiss: PropTypes.bool,
};

export default ErrorAlert;
